import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail } from "lucide-react";
import { adminAxios, auth, formatApiError } from "@/lib/admin_api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (auth.getToken()) {
      adminAxios.get("/auth/me").then(() => navigate("/admin/dashboard")).catch(() => auth.clear());
    }
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await adminAxios.post("/auth/login", form);
      auth.setToken(data.access_token);
      toast.success(`Welcome back, ${data.user?.name || "Admin"}.`);
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background px-6"
      data-testid="admin-login-page"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-surface border border-border rounded-2xl p-8 md:p-10 shadow-[0_30px_70px_-30px_rgba(26,54,38,0.25)]"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="h-11 w-11 rounded-full bg-primary flex items-center justify-center">
            <Lock size={18} className="text-accent" strokeWidth={1.7} />
          </span>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-accent">
              Admin
            </p>
            <h1 className="font-heading text-2xl font-semibold text-primary">
              Sign in
            </h1>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="email" className="mb-2 block text-sm font-medium text-foreground">
              Email
            </Label>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="h-12 pl-10"
                placeholder="admin@gamya.energy"
                data-testid="admin-email-input"
                required
              />
            </div>
          </div>
          <div>
            <Label htmlFor="password" className="mb-2 block text-sm font-medium text-foreground">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="h-12"
              placeholder="••••••••"
              data-testid="admin-password-input"
              required
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="mt-7 w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
          data-testid="admin-login-submit"
        >
          {loading ? "Signing in..." : "Sign in"}
        </Button>

        <p className="mt-5 text-xs text-muted-foreground text-center">
          Admin access only · Gamya Consultancy & Electric Solutions
        </p>
      </form>
    </div>
  );
}
